<template>
  <div ref="outerContainerRef" class="tw-w-full">
    <Multiselect
      ref="multiselectRef"
      v-model="value"
      v-bind="$attrs"
      :can-clear="canClear"
      :clear-on-select="clearOnSelect"
      :class="classes"
      :classes="multiselectClasses"
      :disabled="disabled"
      :hide-selected="hideSelected"
      :infinite="infinite"
      :loading="loading"
      :mode="mode"
      :no-options-text="noOptionsText"
      :no-results-text="noResultsText"
      :searchable="searchable"
      :close-on-select="closeOnSelect"
      :open-direction="openDirection"
      :options="options"
      :placeholder="placeholder"
      :required="required"
      :internal-search="true"
      :preserve-search="true"
      :reset-after="false"
      @open="handleOpen"
      @close="handleClose"
      @keydown="handleKeyDown"
      @search-change="(newQuery) => (currentQuery = newQuery)"
    >
      <template #option="{ option }">
        <slot name="option" :option="castOption(option)"></slot>

        <div
          class="tw-flex tw-items-center tw-space-x-2"
          :style="{ paddingLeft: option.icon || !hasIcons ? '0px' : '22px' }"
        >
          <i v-if="option.icon" :class="option.icon"></i>
          <span>{{ option.label }}</span>
        </div>
      </template>

      <template v-if="supportsTalkingPoints" #tag="{ option }">
        <div
          class="
            tw-flex
            tw-items-center
            tw-bg-gray-200
            tw-px-3
            tw-py-0.5
            tw-rounded-full
            tw-space-x-2
            tw-mr-1
            tw-text-[12px]
          "
        >
          <i v-if="option.icon" :class="option.icon"></i>
          <span>{{ option.label }}</span>
          <i class="fa fa-times" @click="removeOption(option)"></i>
        </div>
      </template>

      <template #beforelist>
        <slot name="beforelist"></slot>
      </template>

      <template #afterlist>
        <slot name="afterlist"></slot>
      </template>

      <template #noresults>
        <slot name="noresults" :query="currentQuery"></slot>
      </template>
    </Multiselect>
    <SfInputError v-if="error" class="!tw-mt-2">
      {{ error }}
    </SfInputError>
  </div>
</template>

<script setup lang="ts" generic="I, MV">
import Multiselect from '@vueform/multiselect'
import { useVModel } from '@vueuse/core'
import {
  computed,
  onBeforeUnmount,
  ref,
  useAttrs,
  watch,
  watchEffect,
} from 'vue'

import SfInputError from '@app/shared/vue/components/sf-input/sf-input-error.vue'
import { isFeatureSupported } from '@app/adapters/silverfin'

// eslint-disable-next-line no-undef
defineOptions({
  name: 'SfMultiselect',
  inheritAttrs: false,
})

interface Option {
  value: string | number
  label: string
  icon?: string
}

interface Props {
  appendToBody?: boolean
  canClear?: boolean
  canSelectOnTab?: boolean
  clearOnSelect?: boolean
  closeOnSelect?: boolean
  disabled?: boolean
  focused?: number
  error?: string
  hideSelected?: boolean
  infinite?: boolean
  loading?: boolean
  options: I[]
  openDirection?: 'top' | 'bottom'
  placeholder?: string
  mode?: 'single' | 'multiple' | 'tags'
  modelValue: MV
  noOptionsText?: string
  noResultsText?: string
  required?: boolean
  searchable?: boolean
  variant?: 'default' | 'sf-input'
  wideDropdown?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  focused: 0,
  error: '',
  openDirection: 'bottom',
  placeholder: '',
  searchable: true,
  mode: 'tags',
  noOptionsText: undefined,
  noResultsText: undefined,
  variant: 'default',
  filterField: '',
  wideDropdown: true,
})

const $attrs = useAttrs()

const emit =
  defineEmits<{
    open: []
    close: []
    'update:modelValue': [value: MV]
    focus: []
    'tab-back': [e: Event]
    'tab-forward': [e: Event]
  }>()

const supportsTalkingPoints = isFeatureSupported('supports_talking_points')
const internalOpen = ref(false)
const outerContainerRef = ref<HTMLElement | null>(null)
const mustFocus = ref(false)
const multiselectRef =
  ref<{
    select: (option: I) => void
    isPointed: (option: I) => boolean
    isOpen: boolean
    $el: HTMLElement
  } | null>(null)
const inputRef = ref<HTMLElement | null>(null)
const queueAutoselect = ref(false)
const currentQuery = ref('')

const multiselectClasses = computed(() =>
  props.variant === 'sf-input'
    ? {
        fakeInput: 'tw-px-4',
      }
    : {}
)
const value = useVModel(props, 'modelValue', emit, {
  passive: true,
  deep: true,
})

function castOption(option: I): I {
  return option as I
}

const classes = computed(() => {
  return {
    [`multiselect-${props.variant}`]: !props.error,
    [`multiselect-${props.variant}--error`]: props.error,
    [`multiselect-${props.variant}--disabled`]: props.disabled,
    ['multiselect-wide-dropdown']: props.wideDropdown,
  }
})

const hasIcons = computed(() => {
  return props.options.some((option) => (option as Option).icon)
})

function handleOpen() {
  emit('open')
  internalOpen.value = true

  // Get the dimensions of the container to position it correctly when the dropdown opens
  // This is necessary because the modal content is scrollable and we need to position the dropdown relative to the container
  // append-to-body from @vueform/multiselect is experimental and does not properly work so this is a workaround
  if (outerContainerRef.value && props.appendToBody) {
    const box = outerContainerRef.value.getBoundingClientRect()
    const left = box.left
    const top = box.top
    const width = box.width

    outerContainerRef.value.style.position = 'fixed'
    outerContainerRef.value.style.left = `${left}px`
    outerContainerRef.value.style.width = width + 'px'
    outerContainerRef.value.style.top = `${top}px`
    outerContainerRef.value.style.zIndex = '99999'
  }
}

function handleClose() {
  emit('close')
  internalOpen.value = false

  if (outerContainerRef.value && props.appendToBody) {
    outerContainerRef.value.style.position = 'static'
    outerContainerRef.value.style.left = 'auto'
    outerContainerRef.value.style.width = 'auto'
    outerContainerRef.value.style.top = 'auto'
    outerContainerRef.value.style.zIndex = 'auto'
  }
}

function handleFocus() {
  emit('focus')
}

// BESPOKE functionality for TAB/Enter key special handling
function handleKeyDown(event: Event) {
  const effectiveEvent = event as KeyboardEvent

  // Does not support any special TAB handling
  if (!props.canSelectOnTab) {
    return
  }

  // Just passing through the element with TAB, emit the events for interested parties
  if (effectiveEvent.key === 'Tab' && !internalOpen.value) {
    if (effectiveEvent.shiftKey) {
      emit('tab-back', event)
    } else {
      emit('tab-forward', event)
    }

    return
  }

  // The dropdown is open but we navigated with shift+TAB, revert to the browser's default behavior
  if (
    effectiveEvent.shiftKey &&
    effectiveEvent.key === 'Tab' &&
    internalOpen.value
  ) {
    return
  }

  if (effectiveEvent.key === 'Tab' && internalOpen.value) {
    effectiveEvent.preventDefault()
    effectiveEvent.stopPropagation()

    const pointedOption = props.options.find((option) =>
      multiselectRef.value?.isPointed(option)
    )

    // Select pointed-item otherwise 1st option
    multiselectRef.value?.select(pointedOption || props.options[0])
  }
}

function removeOption(option: I) {
  if (!Array.isArray(value.value)) {
    //if not array, stop here
    return false
  }

  const index = value.value.findIndex(
    (item) => item === option || item === (option as Option)?.value
  )

  if (index !== -1) {
    value.value.splice(index, 1)
    emit('update:modelValue', value.value as MV)
  }
}

watchEffect(() => {
  if (multiselectRef?.value?.$el) {
    const input = multiselectRef.value.$el.querySelector('input')

    if (input) {
      inputRef.value = input
      input.addEventListener('focus', handleFocus)
    }

    if (mustFocus.value && input) {
      mustFocus.value = false
      input.focus()
    }
  }
})

watch(
  () => props.focused,
  (newValue, oldValue) => {
    if (newValue && newValue !== oldValue) {
      mustFocus.value = true
    }
  },
  { immediate: true }
)

// BESPOKE functionality for TAB/Enter key special handling
// Make sure tab auto-select only happens on latest async data
watchEffect(() => {
  if (
    queueAutoselect.value &&
    multiselectRef.value &&
    !props.loading &&
    props.options.length > 0
  ) {
    queueAutoselect.value = false

    // Select 1st option
    multiselectRef.value?.select(props.options[0])
  }
})

onBeforeUnmount(() => {
  if (inputRef.value) {
    inputRef.value.removeEventListener('focus', handleFocus)
  }
})
</script>

<style lang="scss">
@import '@vueform/multiselect/themes/default.css';
@import 'silverfin-ui-kit/src/scss/tooling';
@import 'app/javascript/scss/multiselect';

@mixin default {
  /* dropdown */
  --ms-font-size: 14px;
  --ms-py: 7px;
  --ms-px: 10px;
  --ms-caret-color: #{-color('text', 'dark')};
  --ms-clear-color: #{-color('text', 'dark')};
  --ms-bg-disabled: #{-color('neutral', 'subtle-1')};

  /* tags */
  --ms-tag-font-size: 11px;
  --ms-tag-line-height: 14.3px;
  --ms-tag-font-weight: 400;
  --ms-tag-bg: #{-color('snow', 'dark')};
  --ms-tag-bg-disabled: #{-color('neutral', 'dark-2')};
  --ms-tag-color: black;
  --ms-tag-color-disabled: white;
  --ms-tag-radius: 110px;

  /* options */
  --ms-option-font-size: 13px;
  --ms-option-line-height: 14px;
  --ms-option-bg-selected: transparent;
  --ms-option-bg-selected-pointed: transparent;
  --ms-option-bg-disabled: #{-color('neutral', 'subtle-1')};
  --ms-option-color-disabled: #d1d5db;
  --ms-option-bg-selected-disabled: #{-color('neutral', 'subtle-1')};
  --ms-option-color-selected-disabled: #d1fae5;
  --ms-spinner-color: #{-color('primary')};
}

@mixin sf-input {
  min-width: 100%;

  --ms-font-size: 14px;
  --ms-border-color: #{-color('snow', 'darker')};
  --ms-dropdown-border-color: #{-color('snow', 'darker')};
  --ms-py: 4px;
  --ms-px: 6px;
  --ms-radius: 4px;
  --ms-option-font-size: 14px;
  --ms-caret-color: #{-color('text', 'dark')};
  --ms-clear-color: #{-color('text', 'dark')};
  --ms-option-color-selected: #{-color('text', 'dark')};
  --ms-option-bg-selected: #{-color('neutral', 'lighter-1')};
  --ms-option-color-selected-pointed: #{-color('text', 'dark')};
  --ms-option-bg-selected-pointed: #{-color('neutral', 'lighter-2')};
  --ms-placeholder-color: #{-color('text', 'light')};

  /* tags */
  --ms-tag-font-size: 11px;
  --ms-tag-line-height: 12px;
  --ms-tag-font-weight: 400;
  --ms-tag-bg: #{-color('snow', 'dark')};
  --ms-tag-bg-disabled: #{-color('neutral', 'dark-2')};
  --ms-tag-color: black;
  --ms-tag-color-disabled: white;
  --ms-tag-radius: 110px;
  --ms-tag-my: 0;

  /* options */
  --ms-option-line-height: 14px;
  --ms-spinner-color: #{-color('primary')};

  input[type='text'] {
    padding: 0 6px;

    &.multiselect-tags-search {
      padding: 0 0 6px;
    }
  }
}

@mixin disabled {
  --ms-caret-color: #{-color('text', 'lighter')};
  --ms-clear-color: #{-color('text', 'lighter')};
  --ms-option-color-selected-pointed: #{-color('text', 'lighter')};
  --ms-placeholder-color: #{-color('text', 'lighter')};
  --ms-option-color-selected: #{-color('text', 'lighter')};

  .multiselect-single-label-text {
    color: #{-color('text', 'lighter')};
  }
}

.multiselect {
  &-dropdown {
    z-index: 99999;
  }

  &-default {
    @include default;

    &--error {
      @include default;

      --ms-border-color: #{-color('danger')};
    }

    &--disabled {
      @include disabled;
    }
  }

  &-sf-input {
    @include sf-input;

    &--error {
      @include sf-input;

      --ms-border-color: #{-color('danger')};
    }

    &--disabled {
      @include disabled;
    }
  }

  &-single {
    margin-left: 0;
    padding-left: 0;

    .multiselect-search {
      left: 10px !important;
    }
  }
}
</style>
